// One database: its size, who may connect to it, and what the console did to it.
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import {
  useCloudDatabaseQuery,
  useResizeDatabaseMutation,
  useTrustSourceMutation,
  useUntrustSourceMutation,
} from '@/client/api/cloud/cloud.query';
import { TrustedSource, TrustedSourceType } from '@/client/types/cloud';
import {
  AccessDenied,
  AuditCard,
  CLOUD_BASE,
  Card,
  ConsoleLink,
  EmptyRow,
  ErrorNotice,
  FetchingChip,
  Field,
  FormDialog,
  FormField,
  HealthPill,
  HealthReasons,
  INPUT_CLASS,
  LoadingNote,
  PageShell,
  Th,
  fmtTime,
} from './cloudUi';

const SOURCE_LABEL: Record<TrustedSourceType, string> = {
  app: 'App',
  droplet: 'Droplet',
  ip_addr: 'IP address or range',
  k8s: 'Kubernetes cluster',
  tag: 'Tag',
};

type Dialog =
  | { kind: 'resize'; size: string }
  | { kind: 'trust' }
  | { kind: 'untrust'; source: TrustedSource };

const CloudDatabasePage: React.FC = () => {
  const { id = '' } = useParams<{ id: string }>();
  const viewPerm = useHasPermission('cloud.view');
  const managePerm = useHasPermission('cloud.manage');
  const canView = viewPerm.isSuperAdmin || viewPerm.hasPermission;
  const canManage = managePerm.isSuperAdmin || managePerm.hasPermission;
  const q = useCloudDatabaseQuery(id, canView);
  const d = q.data;
  useBreadcrumbs([
    { label: 'Cloud', href: CLOUD_BASE },
    { label: 'Resources', href: `${CLOUD_BASE}/resources` },
    { label: d?.database.name ?? 'Database' },
  ]);

  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [size, setSize] = useState('');
  const [sourceType, setSourceType] = useState<TrustedSourceType>('app');
  const [sourceValue, setSourceValue] = useState('');
  const resize = useResizeDatabaseMutation();
  const trust = useTrustSourceMutation();
  const untrust = useUntrustSourceMutation();
  const close = () => {
    setDialog(null);
    resize.reset();
    trust.reset();
    untrust.reset();
  };

  if (viewPerm.isLoading || (q.isLoading && !d)) {
    return (
      <PageShell title="Cloud" description="">
        <LoadingNote>Reading this database and who may connect to it.</LoadingNote>
        <Skeleton className="h-64 w-full" />
      </PageShell>
    );
  }
  if (!canView) {
    return (
      <PageShell title="Cloud" description="">
        <AccessDenied />
      </PageShell>
    );
  }
  if (!d) {
    return (
      <PageShell title="Cloud" description="">
        <ErrorNotice error={q.error} onRetry={() => q.refetch()} />
      </PageShell>
    );
  }

  const db = d.database;
  const sources = d.trusted_sources;
  const open = sources !== null && sources.length === 0;
  const choices = sourceType === 'app' ? d.sources.apps : d.sources.servers;
  const picked = size || db.size || '';
  return (
    <PageShell
      title={db.name}
      description={['Database', `${db.engine} ${db.version}`, db.region].filter(Boolean).join(', ')}
      actions={
        <div className="flex items-center gap-3">
          <FetchingChip />
          <HealthPill level={db.health.level} />
          <ConsoleLink resource={db} />
        </div>
      }
    >
      {q.isError && <ErrorNotice error={q.error} onRetry={() => q.refetch()} />}

      <Card title="Health">
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
          <Field label="Status">
            <HealthPill level={db.health.level} />
            <HealthReasons health={db.health} className="mt-1" />
            <p className="mt-1 text-xs text-gray-500">{db.status}</p>
          </Field>
          <Field label="Size">
            {db.size}
            {db.nodes && db.nodes > 1 ? ` x ${db.nodes}` : ''}
          </Field>
          <Field label="Storage">
            {db.storage_mib ? `${Math.round(db.storage_mib / 1024)} GB` : '—'}
          </Field>
          <Field label="Maintenance window">
            <span className="capitalize">
              {db.maintenance?.day ? `${db.maintenance.day} ${db.maintenance.hour} UTC` : '—'}
            </span>
          </Field>
          <Field label="Created">{fmtTime(db.created_at)}</Field>
        </div>
        <p className="mt-4 border-t border-gray-100 pt-3 text-xs text-gray-500">
          The provider gives no CPU, memory or connection figures for a managed database through
          its API, so this page cannot chart them. They are in its console.
        </p>
      </Card>

      {canManage && d.sizes.length > 0 && (
        <Card title="Resize" note="changes what we are billed">
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={picked}
              onChange={(e) => setSize(e.target.value)}
              aria-label="New size"
              className={INPUT_CLASS}
            >
              {d.sizes.map((option) => (
                <option key={option} value={option}>
                  {option}
                  {option === db.size ? ' (current)' : ''}
                </option>
              ))}
            </select>
            <Button
              size="sm"
              variant="outline"
              disabled={picked === db.size}
              onClick={() => setDialog({ kind: 'resize', size: picked })}
            >
              Resize
            </Button>
          </div>
        </Card>
      )}

      <Card
        title="Trusted sources"
        note="what may open a connection, before the password is even asked for"
        flush
      >
        {sources === null ? (
          <p className="p-4 text-sm text-amber-800">
            The trusted sources could not be read. That is not the same as there being none.
          </p>
        ) : (
          <>
            {open && (
              <p className="border-b border-amber-100 bg-amber-50 p-4 text-sm text-amber-800">
                There are none, so this database accepts a connection from any address that has
                the password. Adding the first one makes it refuse every source that is not
                listed.
              </p>
            )}
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-left text-xs text-gray-500">
                  <Th>Source</Th>
                  <Th>Type</Th>
                  <Th>Added</Th>
                  <Th>{null}</Th>
                </tr>
              </thead>
              <tbody>
                {sources.length === 0 && <EmptyRow cols={4}>No trusted sources</EmptyRow>}
                {sources.map((s) => (
                  <tr key={s.uuid} className="border-b border-gray-50 last:border-0">
                    <td className="px-4 py-2 text-gray-900">
                      {s.name ?? s.value}
                      {s.name && (
                        <span className="block font-mono text-xs text-gray-500">{s.value}</span>
                      )}
                      {!s.name && (s.type === 'app' || s.type === 'droplet') && (
                        <span className="block text-xs text-amber-700">
                          not in our project, or deleted
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-gray-600">{SOURCE_LABEL[s.type] ?? s.type}</td>
                    <td className="px-4 py-2 text-gray-600">{fmtTime(s.created_at)}</td>
                    <td className="px-4 py-2 text-right">
                      {canManage && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600"
                          onClick={() => setDialog({ kind: 'untrust', source: s })}
                        >
                          Remove
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {canManage && (
              <div className="border-t border-gray-100 p-4">
                <Button size="sm" variant="outline" onClick={() => setDialog({ kind: 'trust' })}>
                  Add a trusted source
                </Button>
              </div>
            )}
          </>
        )}
      </Card>

      <AuditCard rows={d.audit} empty="Nothing has been done to this database from the console" />

      {dialog?.kind === 'resize' && (
        <FormDialog
          title={`Resize ${db.name}`}
          description={`Changes it from ${db.size} to ${dialog.size}. The provider moves the database to new machines, which briefly drops connections, and the bill changes with the size.`}
          confirmLabel="Resize"
          destructive
          confirmText={db.name}
          busy={resize.isPending}
          error={resize.error}
          onClose={close}
          onConfirm={(reason) =>
            resize.mutate({ databaseId: db.id, size: dialog.size, reason }, { onSuccess: close })
          }
        />
      )}
      {dialog?.kind === 'trust' && (
        <FormDialog
          title={`Add a trusted source to ${db.name}`}
          description={
            open
              ? 'This is the first one. From the moment it is added, the database refuses every app, server and address that is not on this list. Add everything that uses this database, or it loses its database.'
              : 'Lets one more source connect. Everything already listed keeps its access.'
          }
          confirmLabel={open ? 'Add and restrict access' : 'Add'}
          destructive={open}
          confirmText={open ? db.name : undefined}
          valid={sourceValue.trim().length > 0}
          busy={trust.isPending}
          error={trust.error}
          onClose={close}
          onConfirm={(reason) =>
            trust.mutate(
              {
                databaseId: db.id,
                type: sourceType,
                value: sourceValue.trim(),
                reason,
                confirmed: open,
              },
              {
                onSuccess: () => {
                  setSourceValue('');
                  close();
                },
              },
            )
          }
        >
          <FormField label="Type">
            <select
              value={sourceType}
              onChange={(e) => {
                setSourceType(e.target.value as TrustedSourceType);
                setSourceValue('');
              }}
              className={`${INPUT_CLASS} mt-1 w-full`}
            >
              <option value="app">App</option>
              <option value="droplet">Droplet</option>
              <option value="ip_addr">IP address or range</option>
            </select>
          </FormField>
          {sourceType === 'ip_addr' ? (
            <FormField label="Address" hint="One address, or a range such as 10.114.0.0/20.">
              <input
                value={sourceValue}
                onChange={(e) => setSourceValue(e.target.value)}
                className={`${INPUT_CLASS} mt-1 w-full font-mono`}
              />
            </FormField>
          ) : (
            <FormField label={SOURCE_LABEL[sourceType]} hint="Only what is in our project.">
              <select
                value={sourceValue}
                onChange={(e) => setSourceValue(e.target.value)}
                className={`${INPUT_CLASS} mt-1 w-full`}
              >
                <option value="">Choose one</option>
                {choices.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </FormField>
          )}
        </FormDialog>
      )}
      {dialog?.kind === 'untrust' && (
        <FormDialog
          title={`Stop trusting ${dialog.source.name ?? dialog.source.value}`}
          description={
            sources?.length === 1
              ? 'This is the last trusted source. Removing it does not lock the database: it opens it, to any address that has the password.'
              : 'It will no longer be able to connect to this database. If it still uses the database, it loses it at once.'
          }
          confirmLabel={sources?.length === 1 ? 'Remove and open access' : 'Remove'}
          destructive
          confirmText={db.name}
          busy={untrust.isPending}
          error={untrust.error}
          onClose={close}
          onConfirm={(reason) =>
            untrust.mutate(
              {
                databaseId: db.id,
                uuid: dialog.source.uuid,
                reason,
                confirmed: sources?.length === 1,
              },
              { onSuccess: close },
            )
          }
        />
      )}
    </PageShell>
  );
};

export default CloudDatabasePage;
